/**
 * Compress an image file using canvas.
 * Profile photos → max 800px, listing photos → max 1200px.
 * Returns a compressed File object.
 */
export async function compressImage(file, { maxWidth = 1200, maxSizeMB = 5, quality = 0.8 } = {}) {
  // Size check before compression
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > maxSizeMB) {
    throw new Error(`Image is too large (${fileSizeMB.toFixed(1)}MB). Maximum allowed is ${maxSizeMB}MB.`);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;

      // Skip compression if already small enough
      if (width <= maxWidth && file.size < 500 * 1024) {
        resolve(file);
        return;
      }

      // Scale down if larger than maxWidth
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Compression failed')); return; }
          const compressed = new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() });
          resolve(compressed);
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

/** Profile photo: max 2MB, resize to 800px */
export function compressProfilePhoto(file) {
  return compressImage(file, { maxWidth: 800, maxSizeMB: 2, quality: 0.8 });
}

/** Listing photo: max 5MB, resize to 1200px */
export function compressListingPhoto(file) {
  return compressImage(file, { maxWidth: 1200, maxSizeMB: 5, quality: 0.8 });
}
